import React, { useState, useMemo } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Checkbox,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import { useStateContext } from "contexts/ContextProvider";
import { normalizePhoneNumber } from "helpers/ServiceHelpers";

const LinguistsPayments1099Process = ({
  open,
  onClose,
  linguistPayments,
  selectedMonths,
  selectedYear,
}) => {
  const theme = useTheme();
  const { showSnackbar } = useStateContext();

  const [step, setStep] = useState(1);
  const [selectedLinguists, setSelectedLinguists] = useState(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");

  // Filter linguists earning more than $600
  const eligibleLinguists = useMemo(() => {
    return (linguistPayments || [])
      .filter((linguist) => parseFloat(linguist.LinguistTotal) >= 600)
      .sort((a, b) => b.LinguistTotal - a.LinguistTotal);
  }, [linguistPayments]);

  // Filter by search
  const filteredLinguists = useMemo(() => {
    if (!searchFilter.trim()) return eligibleLinguists;
    return eligibleLinguists.filter(
      (l) =>
        l.LinguistName.toLowerCase().includes(searchFilter.toLowerCase()) ||
        l.PhoneNumber?.includes(searchFilter) ||
        l.Address?.toLowerCase().includes(searchFilter.toLowerCase()),
    );
  }, [eligibleLinguists, searchFilter]);

  // Initialize selected linguists on open
  React.useEffect(() => {
    if (open && eligibleLinguists.length > 0) {
      const allIds = new Set(eligibleLinguists.map((l) => l.LinguistID));
      setSelectedLinguists(allIds);
    }
  }, [open, eligibleLinguists]);

  const handleSelectLinguist = (linguistId) => {
    const newSelected = new Set(selectedLinguists);
    if (newSelected.has(linguistId)) {
      newSelected.delete(linguistId);
    } else {
      newSelected.add(linguistId);
    }
    setSelectedLinguists(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedLinguists.size === filteredLinguists.length) {
      setSelectedLinguists(new Set());
    } else {
      const allIds = new Set(filteredLinguists.map((l) => l.LinguistID));
      setSelectedLinguists(allIds);
    }
  };

  const handleEmailClick = (event, linguist) => {
    event.stopPropagation();
    // Placeholder for email functionality
    showSnackbar(
      `Email feature coming soon for ${linguist.LinguistName}`,
      theme,
    );
  };

  const handleGenerateAndExport = async () => {
    if (selectedLinguists.size === 0) {
      showSnackbar("Please select at least one linguist", theme);
      return;
    }

    setIsGenerating(true);
    try {
      const selectedData = eligibleLinguists.filter((l) =>
        selectedLinguists.has(l.LinguistID),
      );

      // Generate XLSX file using exceljs
      const ExcelJS = require("exceljs");
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Linguists 1099");

      // Add headers
      worksheet.columns = [
        { header: "Name", key: "Name", width: 30 },
        { header: "Phone Number", key: "PhoneNumber", width: 20 },
        { header: "Address", key: "Address", width: 40 },
        { header: "Email", key: "Email", width: 30 },
        { header: "Amount", key: "Amount", width: 15 },
      ];

      // Add data rows
      selectedData.forEach((linguist) => {
        worksheet.addRow({
          Name: linguist.LinguistName || "",
          PhoneNumber: linguist.PhoneNumber || "",
          Address: linguist.Address || "",
          Email: linguist.Email || "",
          Amount: `$${parseFloat(linguist.LinguistTotal || 0).toFixed(2)}`,
        });
      });

      // Generate and download file
      const fileName = `Linguists_1099_${selectedYear[0]}.xlsx`;
      const buffer = await workbook.xlsx.writeBuffer();
      const url = URL.createObjectURL(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);

      showSnackbar("XLSX file generated successfully!", theme);
      onClose();
    } catch (error) {
      console.error("Error generating XLSX:", error);
      showSnackbar("Error generating XLSX file", theme);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedLinguists(new Set());
    setSearchFilter("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          backgroundColor: theme.palette.background.alt,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: "1.5rem",
          color: theme.palette.primary.main,
        }}
      >
        Generate 1099 Forms
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {step === 1 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Header Info */}
            <Alert severity="info">
              Found {eligibleLinguists.length} linguist(s) who earned $600+ in{" "}
              {selectedYear?.[0] || "the selected year"}
            </Alert>

            {/* Search Filter */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, phone, or address..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />

            {/* Select All Checkbox */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1,
                backgroundColor: theme.palette.background.default,
                borderRadius: "8px",
              }}
            >
              <Checkbox
                checked={
                  selectedLinguists.size === filteredLinguists.length &&
                  filteredLinguists.length > 0
                }
                indeterminate={
                  selectedLinguists.size > 0 &&
                  selectedLinguists.size < filteredLinguists.length
                }
                onChange={handleSelectAll}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Select All ({selectedLinguists.size}/{filteredLinguists.length})
              </Typography>
            </Box>

            {/* Linguist List */}
            <Box
              sx={{
                maxHeight: "400px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {filteredLinguists.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No linguists found matching your search.
                </Typography>
              ) : (
                filteredLinguists.map((linguist) => (
                  <Card
                    key={linguist.LinguistID}
                    sx={{
                      backgroundColor: theme.palette.background.default,
                      cursor: "pointer",
                      border: selectedLinguists.has(linguist.LinguistID)
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${theme.palette.divider}`,
                      transition: "all 0.2s",
                      minHeight: 120,
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                    }}
                    onClick={() => handleSelectLinguist(linguist.LinguistID)}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        py: 2,
                        px: 2,
                        minHeight: 120,
                      }}
                    >
                      <Checkbox
                        checked={selectedLinguists.has(linguist.LinguistID)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() =>
                          handleSelectLinguist(linguist.LinguistID)
                        }
                      />

                      {/* Linguist Info */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            mb: 0.5,
                          }}
                        >
                          {linguist.LinguistName}
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(2, minmax(260px, 1fr))",
                            columnGap: 2,
                            rowGap: 1,
                            mt: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.6 }}
                          >
                            <strong>Amount:</strong> $
                            {parseFloat(linguist.LinguistTotal).toFixed(2)}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.6 }}
                          >
                            <strong>Phone:</strong>{" "}
                            {linguist.PhoneNumber ? normalizePhoneNumber(linguist.PhoneNumber) : "N/A"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.6 }}
                          >
                            <strong>Address:</strong>{" "}
                            {linguist.Address || "N/A"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.6 }}
                          >
                            <strong>Email:</strong> {linguist.Email || "N/A"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Action Buttons */}
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleEmailClick(e, linguist)}
                          sx={{
                            color: theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <EmailRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>

            {/* Summary */}
            {selectedLinguists.size > 0 && (
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: theme.palette.background.default,
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.primary.main}`,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Selected: {selectedLinguists.size} linguist(s)
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Total Amount: $
                  {eligibleLinguists
                    .filter((l) => selectedLinguists.has(l.LinguistID))
                    .reduce((sum, l) => sum + parseFloat(l.LinguistTotal), 0)
                    .toFixed(2)}
                </Typography>
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        {step === 1 && (
          <Button
            variant="contained"
            onClick={() => setStep(2)}
            disabled={selectedLinguists.size === 0 || isGenerating}
          >
            Next
          </Button>
        )}
      </DialogActions>

      {/* Step 2: Generate and Export */}
      <Dialog
        open={step === 2}
        onClose={() => setStep(1)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: theme.palette.background.alt,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.25rem" }}>
          Generate 1099 Forms
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, my: 2 }}>
            <Alert severity="info">
              Ready to generate 1099 forms for {selectedLinguists.size} linguist
              {selectedLinguists.size !== 1 ? "s" : ""}?
            </Alert>
            <Typography variant="body2" color="textSecondary">
              This will create:
            </Typography>
            <ul style={{ margin: "0.5rem 0" }}>
              <li>Excel file with linguist information and payment amounts</li>
            </ul>
            <Typography variant="body2" color="textSecondary">
              The file will be downloaded to your computer.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setStep(1)}
            disabled={isGenerating}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateAndExport}
            disabled={isGenerating}
            startIcon={isGenerating ? <CircularProgress size={20} /> : null}
          >
            {isGenerating ? "Generating..." : "Generate & Download"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default LinguistsPayments1099Process;
